/**
 * Saneamiento de URLs.
 *
 * Hoy todo el copy vive en src/content/site.ts y es de confianza, así que esto
 * es defensa en profundidad. Deja de serlo en cuanto un CMS, un query param o
 * cualquier entrada de usuario alimente un href: ahí `javascript:` y `data:` en
 * un enlace son XSS directo, y React NO los bloquea por sí solo (sólo emite un
 * warning en desarrollo para `javascript:`).
 *
 * Esquemas permitidos, en lista blanca — nunca lista negra, porque las listas
 * negras se saltan con `java\tscript:`, mayúsculas o entidades HTML.
 */
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** Destino seguro cuando un href se rechaza: no navega a ninguna parte. */
const SAFE_FALLBACK = '#';

export function sanitizeHref(href: string): string {
  const trimmed = href.trim();

  // Anclas y rutas relativas de la propia app: siempre seguras, y son la
  // inmensa mayoría de los enlaces del sitio.
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) return trimmed;

  try {
    // La base sólo se usa para resolver relativas; las absolutas la ignoran.
    const parsed = new URL(trimmed, 'https://togados.do');
    return ALLOWED_PROTOCOLS.has(parsed.protocol) ? trimmed : SAFE_FALLBACK;
  } catch {
    // URL malformada — no la propagamos al DOM.
    return SAFE_FALLBACK;
  }
}

/** True si el enlace sale del propio origen y necesita endurecerse. */
export function isExternal(href: string): boolean {
  if (href.startsWith('#') || href.startsWith('/')) return false;
  return /^[a-z][a-z0-9+.-]*:/i.test(href);
}

/**
 * Atributos de endurecimiento para enlaces que abren en pestaña nueva.
 *
 * `noopener` corta el acceso a `window.opener`, que sin él permite a la página
 * destino redirigir la nuestra a un phishing (tabnabbing inverso). `noreferrer`
 * además impide filtrar la URL de origen. Los navegadores modernos aplican
 * noopener implícitamente con target=_blank, pero los antiguos no.
 */
export function externalLinkAttrs(href: string) {
  return isExternal(href)
    ? ({ target: '_blank', rel: 'noopener noreferrer' } as const)
    : ({} as const);
}
